import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import AuthMenu from '../auth/AuthMenu';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../config/supabase';

interface Profile {
  display_name: string;
}

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, initializeAuth } = useAuthStore();
  const [profile, setProfile] = React.useState<Profile | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  // Add focus event listener to refresh auth state
  useEffect(() => {
    const handleFocus = async () => {
      try {
        // Check if session exists
        const { data: { session } } = await supabase.auth.getSession();
        
        // If we have a session but no user in state, reinitialize auth
        const { user: currentUser } = useAuthStore.getState();
        if (session && !currentUser) {
          console.log('Session found on focus but no user in state - reinitializing auth');
          await initializeAuth();
        } 
        // If we don't have a session, also reinitialize to ensure state is correct
        else if (!session) {
          console.log('No session found on focus – reinitializing auth in 500ms');
          // Add a small delay to allow any pending auth operations to complete
          setTimeout(async () => {
            await initializeAuth();
          }, 500);
        }
      } catch (err) {
        console.error('Error checking session on focus:', err);
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [initializeAuth]);

  // Initialize dark mode from localStorage on mount
  React.useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const isDark = savedTheme === 'dark';
    document.documentElement.classList.toggle('dark', isDark);
  }, []);

  React.useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) {
        setProfile(null);
        return;
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          console.warn('No session found while fetching profile - reinitializing auth');
          await initializeAuth();
          return;
        }

        const { data, error: fetchError } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('id', user.id)
          .single();

        if (fetchError) throw fetchError;
        setProfile(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
        setProfile(null);
      }
    };

    fetchProfile();
  }, [user, initializeAuth]);

  const isHomePage = location.pathname === '/';
  const isGuidedPath = location.pathname.startsWith('/guided');
  const isCenterPage = location.pathname === '/center';
  const showBackButton = !isHomePage && !isGuidedPath && !isCenterPage;

  return (
    <div className="min-h-screen bg-parchment dark:bg-shadow text-ink dark:text-parchment grain-overlay">
      <header className="sticky top-0 z-10 bg-parchment/80 dark:bg-shadow/80 backdrop-blur-sm border-b border-mist dark:border-dusk">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            {!isHomePage && (
              <Link
                to="/"
                className="text-xl font-display font-semibold text-sun dark:text-ember hover:text-ember dark:hover:text-sun transition-colors"
              >
                Tara's Tarot
              </Link>
            )}
          </div>

          <div className="flex items-center space-x-4">
            {user && profile?.display_name && (
              <span className="font-display italic text-lg text-ink/70 dark:text-parchment/70">
                Hello, {profile.display_name}
              </span>
            )}
            {showBackButton && (
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-full hover:bg-vellum dark:hover:bg-dusk focus:outline-none text-ink/60 dark:text-parchment/60"
                aria-label="Go back"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <AuthMenu />
          </div>
        </div>
      </header>

      <main className="relative z-[2] container mx-auto px-4 py-8">
        {children}
      </main>
    </div>
  );
};

export default AppLayout;